import enMessages from '@/i18n/en.json'
import enCaMessages from '@/i18n/en-CA.json'
import nbNoMessages from '@/i18n/nb-NO.json'
import svSeMessages from '@/i18n/sv-SE.json'
import frFrMessages from '@/i18n/fr-FR.json'
import frCaMessages from '@/i18n/fr-CA.json'
import deDeMessages from '@/i18n/de-DE.json'
import esEsMessages from '@/i18n/es-ES.json'
import itItMessages from '@/i18n/it-IT.json'
import plPlMessages from '@/i18n/pl-PL.json'
import nlNlMessages from '@/i18n/nl-NL.json'
import fiFiMessages from '@/i18n/fi-FI.json'
import daDkMessages from '@/i18n/da-DK.json'
import csCzMessages from '@/i18n/cs-CZ.json'

const messagesByLocale: Record<string, Record<string, string>> = {
  en: enMessages,
  'en-US': enMessages,
  'en-GB': enMessages,
  'en-CA': enCaMessages,
  enUS: enMessages,
  enGB: enMessages,
  enCA: enCaMessages,
  'nb-NO': nbNoMessages,
  nbNO: nbNoMessages,
  'sv-SE': svSeMessages,
  svSE: svSeMessages,
  'fr-FR': frFrMessages,
  'fr-CA': frCaMessages,
  frFR: frFrMessages,
  frCA: frCaMessages,
  'de-DE': deDeMessages,
  deDE: deDeMessages,
  'es-ES': esEsMessages,
  esES: esEsMessages,
  'it-IT': itItMessages,
  itIT: itItMessages,
  'pl-PL': plPlMessages,
  plPL: plPlMessages,
  'nl-NL': nlNlMessages,
  nlNL: nlNlMessages,
  'fi-FI': fiFiMessages,
  fiFI: fiFiMessages,
  'da-DK': daDkMessages,
  daDK: daDkMessages,
  'cs-CZ': csCzMessages,
  csCZ: csCzMessages,
}

/**
 * Translate a message by key. Works in both server and client components.
 *
 * @param locale - BCP 47 locale or Sanity camelCase key (e.g. "nb-NO" or "nbNO")
 * @param id - Dot-separated message key (e.g. "product.priceNote")
 * @param defaultMessage - English fallback used when the key is missing
 * @param values - Optional placeholder map; replaces `{name}` tokens in the message
 *
 * @example
 *   t(locale, 'product.articleNumber', 'Article number')
 *   t(locale, 'campaign.period', 'Campaign period: {period}', {period: '5–14 May 2026'})
 */
export function t(
  locale: string | undefined,
  id: string,
  defaultMessage: string,
  values?: Record<string, string | number>,
): string {
  const messages = messagesByLocale[locale || ''] || messagesByLocale.en || {}
  const template = messages[id] || defaultMessage
  if (!values) return template
  return template.replace(/\{(\w+)\}/g, (_match, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : `{${key}}`,
  )
}

/**
 * Compatibility exports for components synced from sanity-nextjs-boilerplate
 * (pagination-nav.tsx), where one site has one locale. Matches the
 * boilerplate's value ('nb') so the synced component and its shared tests run
 * unmodified here. jotul is multi-site, so callers always pass translated
 * labels for the site they render (SearchResultsView does); these defaults
 * never reach a visitor.
 */
export const SITE_LOCALE = 'nb'

export const isNorwegian = (locale: string | undefined | null): boolean =>
  /^(nb|nn|no)\b/i.test(locale || '')
